/**
 * `/member-view/:token` — ONE member's own detail page, with no dashboard login.
 *
 * The Clavis demo frames this so a visitor can watch the studio's side of their
 * own conversation fill in. Its viewer has no Supabase session and no
 * `staff_allowlist` row, so it sits outside <RequireAuth> — the same shape and
 * the same reasoning as `/trainer-pay/:token`, and registered in
 * `page-registry.ts` UNGATED_ROUTES rather than merely omitted.
 *
 * The token is the capability. It is an HMAC the receiver minted after the
 * member proved identity by OTP, and its signed payload names the studio and
 * the member — so this page reads the member id straight out of it rather than
 * from the URL. That is safe to do CLIENT-side: the payload was never secret,
 * and the signature is what makes it unforgeable. The server re-verifies on
 * every request and refuses any member but the one named, so a visitor who
 * edits the decoded id gains nothing.
 *
 * Renders the REAL MemberDetail, unmodified. That is the whole point: the demo
 * shows the product rather than a drawing of it, and cannot drift from it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MemberDetail } from "./MemberDetail";
import { setCapabilityToken } from "../lib/api";

/**
 * The member id out of the token's payload half.
 *
 * Shape is `base64url("<studio_id>|<member_id>|<expires_ms>").<mac>` — see
 * `member-read-tokens.ts`. Decoding here is a convenience, never a check:
 * nothing is trusted on the strength of it, because every request that uses
 * the id is verified server-side against the same token.
 */
function memberIdFromToken(token: string): string | null {
  const dot = token.indexOf(".");
  if (dot <= 0) return null;
  try {
    const json = atob(token.slice(0, dot).replace(/-/g, "+").replace(/_/g, "/"));
    const parts = json.split("|");
    return parts.length === 3 && parts[1] ? parts[1] : null;
  } catch {
    return null;
  }
}

export function MemberView(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const memberId = useMemo(
    () => (token === undefined ? null : memberIdFromToken(token)),
    [token],
  );

  /* MemberDetail is NOT rendered until the token is actually installed, and
     that gate is load-bearing rather than cosmetic.

     React runs a CHILD's effects before its PARENT's. Rendering MemberDetail
     straight away therefore fired its one-shot load effect — `api.member(id)`
     — before this effect had set the capability token, so `bearer()` fell
     through to the Supabase session, found none, and sent an empty
     Authorization header. The dashboard answered `whoami_unauthorized`, and
     because that load runs once per `id` there was no second attempt: the
     framed pane sat on "Couldn't load this member" forever, while the very
     same token returned 200 to curl. */
  const [armed, setArmed] = useState(false);
  const shellRef = useRef<HTMLDivElement | null>(null);

  /* The demo frames this page beside the member's own chat, which is dark,
     rounded and translucent — while the dashboard is pinned `color-scheme:
     light` on purpose, because an operator's instrument panel should be. This
     class re-themes THIS page and only this page; see "MEMBER-VIEW GLASS" at
     the foot of styles.css.

     On the documentElement rather than a wrapper, so the ground the glass
     cards float on is themed too — glass over a white page is just a grey box.
     Removed on unmount so it cannot leak into a staff session opened in the
     same tab afterwards, exactly like the capability token below. */
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("member-view-glass");
    return () => root.classList.remove("member-view-glass");
  }, []);

  useEffect(() => {
    if (token === undefined) return;
    setCapabilityToken(token);
    setArmed(true);
    // Cleared on unmount so it can never ride along into a staff session
    // opened in the same tab afterwards.
    return () => {
      setArmed(false);
      setCapabilityToken(null);
    };
  }, [token]);

  /* TELLING THE FRAME'S PARENT WHEN THE WAIT IS ACTUALLY OVER (2026-09-12).

     The demo covers this pane with a spinner veil while the page boots, and
     used to lift it on the iframe's own `load` event. That fires when the
     DOCUMENT is ready, which is a whole fetch too early: the veil went out
     over "Loading member…" rather than over the member. So the page says so
     itself, and the veil waits for the message.

     `load` cannot be salvaged for this and nothing on the parent's side can
     replace it — the two are different origins, and the parent is not allowed
     to look in. An explicit postMessage is the sanctioned way across that
     line, and it carries no data: the word is the whole payload, which is also
     why `"*"` is a safe target here.

     The signal is the KPI strip, the first thing MemberDetail lays out once it
     has the member. An error, or no member at all, never paints it — so the
     timer below ends the wait regardless. A visitor who cannot be shown the
     page must be shown the reason, not a spinner that never stops. */
  useEffect(() => {
    if (!armed) return;
    const shell = shellRef.current;
    if (shell === null) return;

    let announced = false;
    const announce = (): void => {
      if (announced) return;
      announced = true;
      try {
        window.parent.postMessage({ type: "clavis-member-view-ready" }, "*");
      } catch {
        // A frame with no parent to tell is not an error worth surfacing.
      }
    };
    const painted = (): boolean => shell.querySelector(".member-kpi-strip") !== null;

    if (painted()) {
      announce();
      return;
    }
    const observer = new MutationObserver(() => {
      if (!painted()) return;
      observer.disconnect();
      announce();
    });
    observer.observe(shell, { childList: true, subtree: true });
    const fallback = window.setTimeout(announce, 4000);
    return () => {
      announced = true;
      observer.disconnect();
      window.clearTimeout(fallback);
    };
  }, [armed]);

  if (token === undefined || memberId === null) {
    return <p style={{ padding: "2rem" }}>This link is not valid.</p>;
  }
  if (!armed) {
    return <p style={{ padding: "2rem" }}>Loading…</p>;
  }
  /* The wrapper exists to supply the margin the staff dashboard's own layout
     would normally give this page. Framed alone it has none, so the KPI strip
     ran straight into all four screen edges. */
  return (
    <div className="member-view-shell" ref={shellRef}>
      <MemberDetail memberId={memberId} />
    </div>
  );
}
